import { useTranslation } from 'react-i18next';

import { Box, Text } from '@bonusly/design-system';

import type { UserAdminBatchConfirmCard, UserAdminBatchItem } from '../../../../lib/bizy/types/ui-blocks';
import { useBizyChatActions } from '../context/actions-context';
import { humanizeFieldToken, UserAdminCard, UserAdminChangeRow, UserAdminFieldRow } from '../lib/user-admin-card';
import type { BizyNodeProps } from './node-props';

/**
 * Confirmation card for a BATCH user-admin action: inviting, updating,
 * deactivating, or reactivating more than one user at once. One card
 * represents the whole batch, confirmed a single time.
 */
export function UserAdminBatchConfirmCardNode({ node }: BizyNodeProps) {
	const block = node as UserAdminBatchConfirmCard;
	const { t } = useTranslation('admin_bizy');
	const { fontSize = 'sm', isReadOnly } = useBizyChatActions();
	const { action, title, consequence, items, skipped, actions } = block.payload;

	const heading = t(`uiBlocks.userAdmin.batchTitle.${action}`, {
		count: items.length,
		defaultValue: title || t('uiBlocks.userAdmin.batchTitleFallback', { count: items.length }),
	});

	return (
		<UserAdminCard title={heading} subtitle={consequence || undefined} actions={actions} isReadOnly={isReadOnly} fontSize={fontSize}>
			<Box display="flex" flexDirection="column" gap={2}>
				{items.map((item, index) => (
					<BatchItemRow key={`${item.title}-${index}`} item={item} fontSize={fontSize} />
				))}
			</Box>

			{skipped && skipped.length > 0 && (
				<Box mt={2} pt={2} borderTop="1px solid" borderColor="border.soft">
					<Text fontSize="xs" color="text.softer" fontWeight="medium">
						{t('uiBlocks.userAdmin.skippedHeading', { count: skipped.length })}
					</Text>
					{skipped.map((entry, index) => (
						<Text key={`${entry.label}-${index}`} fontSize="xs" color="text.softer">
							{entry.reason ? `${entry.label} \u2014 ${entry.reason}` : entry.label}
						</Text>
					))}
				</Box>
			)}
		</UserAdminCard>
	);
}

function BatchItemRow({ item, fontSize }: { item: UserAdminBatchItem; fontSize: 'sm' | 'md' }) {
	const rows = item.rows ?? [];
	const changes = item.changes ?? [];

	return (
		<Box minW={0}>
			<Text fontSize={fontSize} fontWeight="medium" color="text.default" isTruncated title={item.title}>
				{item.title}
			</Text>
			{item.subtitle && (
				<Text fontSize="xs" color="text.softer" isTruncated title={item.subtitle}>
					{item.subtitle}
				</Text>
			)}
			{rows.map((row, index) => (
				<UserAdminFieldRow key={`${row.label}-${index}`} label={row.label} value={row.value || '\u2014'} fontSize={fontSize} />
			))}
			{changes.map((change, index) => (
				<UserAdminChangeRow
					key={`${change.field}-${index}`}
					label={humanizeFieldToken(change.field)}
					before={change.before || '\u2014'}
					after={change.after || '\u2014'}
					fontSize={fontSize}
				/>
			))}
		</Box>
	);
}
